import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { clearTemplates } from '../store/slices/templateSlice';
import { clearFooters } from '../store/slices/footerSlice';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  TagIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { toggleChat } from '../store/slices/chatSlice';
import { INSTITUTION_TYPES } from '../utils/constants';
import logo from '../assets/new_logo.jpeg';
import ChatOverlay from './chat/ChatOverlay';

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { rooms } = useSelector((state) => state.chat);
  const totalUnread = rooms ? rooms.reduce((acc, r) => acc + (r.unreadCount || 0), 0) : 0;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [institutionDropdownOpen, setInstitutionDropdownOpen] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobileDevice(mobile);
    };
    checkMobile();
  }, []);

  const handleLogout = () => {
    // Clear user-specific cached data before logout so next user gets fresh filtered data
    dispatch(clearTemplates());
    dispatch(clearFooters());
    dispatch(logout());
    navigate('/login');
  };

  const hasAccessToInstitutionType = (institutionType) => {
    // Superadmin has access to everything
    if (user?.role === 'superadmin') return true;
    // Check if user has access to this institution type
    return user?.institutionTypes?.includes(institutionType);
  };

  const canManageUsers = user?.role === 'superadmin';

  // Get user's accessible institution types - superadmin gets all by default
  const userInstitutionTypes = user?.role === 'superadmin'
    ? INSTITUTION_TYPES
    : (user?.institutionTypes || []);

  const systemNavigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon, show: true },
    { name: 'Compose Email', href: '/compose-email', icon: EnvelopeIcon, show: user?.role === 'superadmin' || user?.canComposeEmail },
    { name: 'Templates', href: '/templates', icon: DocumentTextIcon, show: user?.role === 'superadmin' },
    { name: 'Footers', href: '/footers', icon: DocumentTextIcon, show: user?.role === 'superadmin' },
    { name: 'Campaigns', href: '/campaigns', icon: EnvelopeIcon, show: user?.role === 'superadmin' },
    { name: 'Contact Categories', href: '/contact-categories', icon: TagIcon, show: user?.role === 'superadmin' },
    { name: 'Activity Logs', href: '/activity-logs', icon: ClipboardDocumentListIcon, show: user?.role === 'superadmin' },
    { name: 'User Activity', href: '/user-activity', icon: ClipboardDocumentListIcon, show: user?.role === 'superadmin' },
    { name: 'Gmail Inbox', href: '/gmail-inbox', icon: EnvelopeIcon, show: user?.role === 'superadmin' },
    { name: 'Users', href: '/users', icon: UserGroupIcon, show: canManageUsers },
  ].filter(item => item.show);

  // Debug log to check user data
  console.log('Current user data:', user);
  console.log('Can compose email:', user?.canComposeEmail);
  console.log('Role:', user?.role);

  const navigation = systemNavigation;

  const isActive = (path) => location.pathname === path;

  // Global Mobile Access Guard
  // Only triggers on mobile devices (UAs) if permission is explicitly false
  if (isMobileDevice && user && user.canAccessMobileView === false) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-full max-w-md space-y-10">
          <div className="flex flex-col items-center">
            <img src={logo} alt="Logo" className="h-24 w-auto rounded-2xl shadow-lg" />
          </div>

          <div className="space-y-6">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-red-50 rounded-full">
              <XMarkIcon className="h-12 w-12 text-red-500" />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">Access Restricted</h1>
              <p className="text-base text-gray-500 font-medium px-4">
                Hi <span className="font-bold text-gray-900">{user.name}</span>, your account is currently restricted from viewing this platform on mobile devices.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-amber-800 text-sm font-semibold">
              Please log in from a Desktop computer to access your dashboard.
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-8 py-5 border border-transparent text-lg font-black rounded-2xl text-white bg-red-600 hover:bg-red-700 shadow-xl transform transition-all active:scale-95"
          >
            <ArrowRightOnRectangleIcon className="h-6 w-6 mr-3" />
            Logout Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white shadow-2xl">
          <div className="flex flex-col items-center justify-center h-20 px-4 bg-blue-600 shadow-md flex-shrink-0">
            <img
              src={logo}
              alt="Twilight Finland Technical Portal"
              className="h-8 w-auto rounded-full object-contain flex-shrink-0 mb-1"
              style={{ borderRadius: '4px' }}
            />
            <p className="text-xs font-semibold text-white text-center">Twilight Finland Technical Portal</p>
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-white hover:bg-blue-700 p-1 rounded-lg transition-all duration-200">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto min-h-0">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${isActive(item.href)
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md border-l-4 border-blue-700'
                  : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 hover:shadow-sm hover:scale-[1.02]'
                  }`}
              >
                <item.icon className={`h-5 w-5 mr-3 ${isActive(item.href) ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'} transition-colors duration-200`} />
                {item.name}
              </Link>
            ))}

            {/* Institution Types Dropdown */}
            {userInstitutionTypes.length > 0 && (
              <div className="space-y-1">
                <button
                  onClick={() => setInstitutionDropdownOpen(!institutionDropdownOpen)}
                  className="group flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-center">
                    <UsersIcon className="h-5 w-5 mr-3 text-gray-500 group-hover:text-blue-600 transition-colors duration-200" />
                    Institution Types
                  </div>
                  {institutionDropdownOpen ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {institutionDropdownOpen && (
                  <div className="ml-8 space-y-1 border-l-2 border-blue-200 pl-3">
                    <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100">
                      {userInstitutionTypes.map((institutionType) => (
                        <Link
                          key={institutionType}
                          to={`/institution/${institutionType ? institutionType.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'unknown'}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`block px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${location.pathname === `/institution/${institutionType ? institutionType.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'unknown'}`
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                        >
                          {institutionType}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col h-full bg-white border-r border-gray-200 shadow-lg">
          <div className="flex flex-col items-center justify-center h-20 px-6 bg-white shadow-md flex-shrink-0">
            <img
              src={logo}
              alt="Twilight Finland Technical Portal"
              className="h-16 w-auto rounded-sm object-contain flex-shrink-0"
              style={{ borderRadius: '4px' }}
            />
            <p className="text-xs font-semibold text-blue-600 text-center mb-4">Twilight Finland Technical Portal</p>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto min-h-0">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${isActive(item.href)
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md border-l-4 border-blue-700'
                  : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 hover:shadow-sm hover:scale-[1.02]'
                  }`}
              >
                <item.icon className={`h-5 w-5 mr-3 ${isActive(item.href) ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'} transition-colors duration-200`} />
                {item.name}
              </Link>
            ))}

            {/* Institution Types Dropdown */}
            {userInstitutionTypes.length > 0 && (
              <div className="space-y-1">
                <button
                  onClick={() => setInstitutionDropdownOpen(!institutionDropdownOpen)}
                  className="group flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-center">
                    <UsersIcon className="h-5 w-5 mr-3 text-gray-500 group-hover:text-blue-600 transition-colors duration-200" />
                    Institution Types
                  </div>
                  {institutionDropdownOpen ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {institutionDropdownOpen && (
                  <div className="ml-8 space-y-1 border-l-2 border-blue-200 pl-3">
                    <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100">
                      {userInstitutionTypes.map((institutionType) => (
                        <Link
                          key={institutionType}
                          to={`/institution/${institutionType ? institutionType.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'unknown'}`}
                          className={`block px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${location.pathname === `/institution/${institutionType ? institutionType.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'unknown'}`
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                        >
                          {institutionType}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-50 flex h-16 bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 shadow-md">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 focus:outline-none lg:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 flex justify-end items-center px-2 sm:px-4 lg:px-8">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Chat Icon - Top Header */}
              <button
                onClick={() => dispatch(toggleChat())}
                className="relative p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all duration-200"
                title="Messages"
              >
                <ChatBubbleLeftRightIcon className="h-6 w-6" />
                {totalUnread > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white">
                    {totalUnread}
                  </span>
                )}
              </button>

              <Link
                to="/profile"
                className="flex items-center px-3 py-2 text-sm font-semibold text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl hover:from-blue-50 hover:to-indigo-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 transition-all duration-200 hover:scale-105 hover:shadow-md"
                title="View Profile"
              >
                <UserCircleIcon className="h-5 w-5 sm:mr-2" />
                <span className="hidden sm:inline">My Profile</span>
              </Link>
              <div className="hidden sm:block text-right bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-lg border border-blue-100">
                <p className="text-sm font-bold text-gray-900">{user?.name}</p>
                <p className="text-xs font-semibold text-blue-600 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 rounded-xl hover:from-red-700 hover:to-red-800 hover:shadow-lg transition-all duration-200 hover:scale-105"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

      <ChatOverlay />
    </div>
  );
};

export default Layout;
